<template>
  <ValidationProvider v-slot="{ errors }" :name="label" :rules="rules">
    <b-field :type="{ 'is-danger': errors.length > 0 || timeError }" :message="errors[0] || timeError">
      <template slot="label">
        {{ label }}
        <b-tooltip v-if="tooltip" type="is-dark" :label="tooltip" multilined>
          <b-icon size="is-small" icon="help-circle-outline"></b-icon>
        </b-tooltip>
      </template>
      <div class="datetime-input-container">
        <b-datepicker
          v-model="dateOnly"
          icon="calendar-today"
          :date-formatter="formatDateOnly"
          @input="onDateChange"
          class="date-picker-part"
          append-to-body
        />
        <b-input
          v-model="timeInput"
          placeholder="HH:MM"
          maxlength="5"
          class="time-input-part"
          @blur="onTimeBlur"
          @keyup.enter="onTimeBlur"
        />
      </div>
    </b-field>
  </ValidationProvider>
</template>

<script>
import { format } from 'date-fns'

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: null
    },
    tooltip: {
      type: String,
      default: null
    },
    rules: {
      type: String,
      default: null
    },
    value: {
      type: [Date, String]
    }
  },
  data() {
    const initialDate = this.setDate(this.value)
    return {
      item: initialDate,
      dateOnly: initialDate ? new Date(initialDate) : null,
      timeInput: initialDate ? format(initialDate, 'HH:mm') : '00:00',
      timeError: null
    }
  },
  watch: {
    value(value) {
      const newDate = this.setDate(value)
      // Prevent infinite loop - only update if the actual time value changed
      if (this.item && newDate && this.item.getTime() === newDate.getTime()) {
        return
      }
      this.item = newDate
      if (newDate) {
        // Only update dateOnly if the date portion actually changed
        const newDateOnly = new Date(newDate)
        newDateOnly.setHours(0, 0, 0, 0)
        const currentDateOnly = this.dateOnly ? new Date(this.dateOnly) : null
        if (currentDateOnly) {
          currentDateOnly.setHours(0, 0, 0, 0)
        }
        if (!currentDateOnly || newDateOnly.getTime() !== currentDateOnly.getTime()) {
          this.dateOnly = new Date(newDate)
        }
        // Only update timeInput if the time portion actually changed
        const newTimeInput = format(newDate, 'HH:mm')
        if (this.timeInput !== newTimeInput) {
          this.timeInput = newTimeInput
        }
      }
    }
  },
  methods: {
    formatDate(date) {
      return format(date, 'dd/MM/yyyy HH:mm')
    },
    formatDateOnly(date) {
      return format(date, 'dd/MM/yyyy')
    },
    setDate(value) {
      if (typeof value === 'string') {
        // CRITICAL: Parse datetime string as LOCAL time, not UTC
        // Database stores times in local timezone but may include 'Z' suffix
        // which would cause new Date() to interpret as UTC and add timezone offset
        const dateStr = value.replace('T', ' ').replace('Z', '').replace(/\.\d+/, '')
        const parts = dateStr.split(/[-\s:]/)
        if (parts.length >= 5) {
          return new Date(
            parseInt(parts[0]),     // year
            parseInt(parts[1]) - 1, // month (0-indexed)
            parseInt(parts[2]),     // day
            parseInt(parts[3]),     // hour
            parseInt(parts[4]),     // minute
            parts[5] ? parseInt(parts[5]) : 0 // second
          )
        }
        // Fallback for non-standard formats
        return new Date(value)
      }
      return value
    },
    parseTimeInput(timeStr) {
      // Accept formats: HH:MM, H:MM, HH:M, HHMM
      if (!timeStr) return null

      // Remove any non-digit/colon characters
      const cleaned = timeStr.replace(/[^0-9:]/g, '')

      let hours, minutes

      if (cleaned.includes(':')) {
        const parts = cleaned.split(':')
        hours = parseInt(parts[0], 10)
        minutes = parseInt(parts[1], 10)
      } else if (cleaned.length >= 3 && cleaned.length <= 4) {
        // HHMM or HMM format
        if (cleaned.length === 4) {
          hours = parseInt(cleaned.substring(0, 2), 10)
          minutes = parseInt(cleaned.substring(2, 4), 10)
        } else {
          hours = parseInt(cleaned.substring(0, 1), 10)
          minutes = parseInt(cleaned.substring(1, 3), 10)
        }
      } else {
        return null
      }

      // Validate ranges
      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null
      }

      return { hours, minutes }
    },
    onTimeBlur() {
      const parsed = this.parseTimeInput(this.timeInput)

      if (!parsed) {
        this.timeError = 'Invalid time format (use HH:MM)'
        return
      }

      this.timeError = null
      // Format the input to standard HH:MM
      this.timeInput = `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`

      this.updateDateTime()
    },
    onDateChange() {
      this.updateDateTime()
    },
    updateDateTime() {
      if (!this.dateOnly) return

      const parsed = this.parseTimeInput(this.timeInput)
      if (!parsed) return

      const newDate = new Date(this.dateOnly)
      newDate.setHours(parsed.hours, parsed.minutes, 0, 0)

      this.item = newDate
      this.$emit('input', newDate)
    }
  }
}
</script>

<style scoped>
.datetime-input-container {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.date-picker-part {
  flex: 1;
}

.time-input-part {
  width: 80px;
  flex-shrink: 0;
}

.time-input-part input {
  text-align: center;
  font-family: monospace;
}
</style>
